import React from "react";
import LegalLayout from "@/components/LegalLayout";

const EFFECTIVE_DATE = "August 22, 2026";

/**
 * PrivacyPolicy — describes what N8One actually collects, how it's used,
 * who it's shared with, and the choices users have. Written in plain
 * language. All third-party services listed here are ones the app
 * genuinely uses (verified against the codebase); no fictional analytics,
 * certifications, or encryption claims.
 */
export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate={EFFECTIVE_DATE}>
      <section data-testid="privacy-intro">
        <p>
          N8One is a reflective wellness app operated by <strong>Essentialist Dispatch</strong>
          (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;N8One&rdquo;). This Privacy Policy explains
          what information we collect when you use N8One, how we use it, when we share it, and
          what choices you have.
        </p>
        <p>
          We wrote this in ordinary language on purpose. If anything is unclear, please email us at{" "}
          <a href="mailto:n8onelife@gmail.com" className="underline">n8onelife@gmail.com</a>.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">What we collect</h2>

        <h3 className="font-display text-lg mt-6">Account &amp; sign-in</h3>
        <p>
          You sign in with Google. During sign-in, Google shares your name, email address, Google
          account ID, and (if you have one) profile picture with us. We store this so we can
          create your account, sign you back in, and label your activity inside your own account.
        </p>

        <h3 className="font-display text-lg mt-6">Profile information</h3>
        <p>
          The display name, username, age group, longevity goals, and any settings you enter into
          your profile — including quiet-hours preferences, notification preferences, and the
          &ldquo;Intentional Self&rdquo; sections (My Longings, My Rooted Truths, My Fear I Am
          Ready To Meet, My Vow To Myself, My Symbol, My Reminders) — are stored so N8One can
          personalize your experience.
        </p>

        <h3 className="font-display text-lg mt-6">Soul Compass responses &amp; results</h3>
        <p>
          When you complete the Soul Compass, your answers, the archetype we surface for you, and
          any pattern-nudges generated from them are stored in your account.
        </p>

        <h3 className="font-display text-lg mt-6">Becoming information</h3>
        <p>
          Everything you enter through the Becoming section — Discover, Understand, Choose,
          Capture — is stored under your account. This includes goals, milestones, My Journey
          entries, daily tracking values (sleep, hydration, movement, meditation, mood), the
          Daily Routine notes and supplements you record, fitness entries, and peptide entries.
        </p>

        <h3 className="font-display text-lg mt-6">Ritual activity</h3>
        <p>
          The rituals you create, adopt, complete, react to, or set reminders for are stored so
          the app can show your history and honor your reminders.
        </p>

        <h3 className="font-display text-lg mt-6">Journal entries</h3>
        <p>
          Your Journal entries — including title, body, mood, and any prompts you used — are
          stored under your account. <strong>Journal entries are private by default.</strong>{" "}
          They are visible only to you unless you intentionally repost or share the content into
          The Collective yourself.
        </p>

        <h3 className="font-display text-lg mt-6">N8 Guide interactions</h3>
        <p>
          When you interact with N8 Guide (our AI companion), we store the messages you send, the
          responses N8 returns, and any personalization signals derived from your Becoming
          information so future responses stay grounded in your journey. These conversations are
          private to your account.
        </p>

        <h3 className="font-display text-lg mt-6">Photos</h3>
        <p>
          You may upload photos in three places:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>
            <strong>Capture (private):</strong> photos you save inside Becoming → Capture are
            private to your account and are never shown to other members.
          </li>
          <li>
            <strong>The Collective (intentionally shared):</strong> photos you attach to a
            check-in or a before/after post become visible on the Collective feed when you post
            them.
          </li>
          <li>
            <strong>Procedure photos (received from a wellness practice):</strong> when you have
            actively accepted a consent request from a wellness practice, they may send
            Before/After procedure photos into your Tracking tab. These are visible only to you
            and to the wellness practice that sent them. You may revoke that consent at any time.
          </li>
        </ul>

        <h3 className="font-display text-lg mt-6">Collective posts, questions, reactions, reports</h3>
        <p>
          The posts, questions, replies, and reactions you make in The Collective are visible to
          other N8One members. Reports you submit about content in The Collective are visible to
          our admin moderation team and to the reporter.
        </p>

        <h3 className="font-display text-lg mt-6">Professional-shared rituals</h3>
        <p>
          If you participate as a wellness professional, rituals you share include the display
          fields you enter (professional type, city, intention, why it&rsquo;s useful, and your
          public display name). These are visible to other members on the shared-rituals feed.
        </p>

        <h3 className="font-display text-lg mt-6">Subscription &amp; billing (Pro)</h3>
        <p>
          If you subscribe to N8One Pro, we store your subscription status, the plan you chose,
          the start and renewal dates, and the transaction identifiers returned by our payment
          processor. Payment card numbers are handled by our payment processor (see &ldquo;Who we
          share information with&rdquo; below) and are not stored on N8One servers.
        </p>

        <h3 className="font-display text-lg mt-6">Device &amp; usage information</h3>
        <p>
          When you use the app, our servers automatically receive standard request information —
          your IP address, browser user-agent, the pages you request, and timestamps — as part of
          how the web works. We use this only to operate the service (routing requests, keeping
          the app running, preventing abuse).
        </p>

        <h3 className="font-display text-lg mt-6">Cookies and similar technologies</h3>
        <p>
          N8One uses two categories of cookies/local storage, both essential to how the app works:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>
            A <strong>session cookie</strong> (<code>session_token</code>) so you stay signed in.
          </li>
          <li>
            A short-lived <strong>OAuth state cookie</strong> during Google sign-in to prevent
            cross-site request forgery.
          </li>
          <li>
            <strong>Browser localStorage</strong> to remember your reminders queue, quiet-hours
            preference, and small UI preferences.
          </li>
        </ul>
        <p className="mt-2">
          N8One does <strong>not</strong> use third-party analytics cookies, advertising trackers,
          or cross-site tracking pixels.
        </p>

        <h3 className="font-display text-lg mt-6">Push notifications</h3>
        <p>
          If you opt in to browser push notifications, your browser generates a subscription
          endpoint that we store so we can send your ritual reminders and consent-flow
          notifications. You can revoke this at any time in your browser settings.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">How we use this information</h2>
        <ul className="list-disc pl-6 mt-2 space-y-2">
          <li>To create and operate your account, sign you back in, and personalize what you see.</li>
          <li>
            To personalize N8 Guide, reflections, weekly recaps, and the tracking heatmap so
            they&rsquo;re grounded in your journey.
          </li>
          <li>To honor the reminders, quiet-hours, and notification preferences you set.</li>
          <li>To show public content (Collective posts, shared rituals) to other members.</li>
          <li>To operate the wellness-practice consent flow you initiate or accept.</li>
          <li>To process Pro subscription payments and manage your subscription status.</li>
          <li>To respond to reports and to moderate content that violates our Community Guidelines.</li>
          <li>To detect abuse, prevent fraud, and keep the service running.</li>
          <li>To comply with our legal obligations.</li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl">Public vs. private information</h2>
        <p className="mt-2">
          We want this to be unmistakable:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-2">
          <li>
            <strong>Private by default:</strong> Journal entries, private Capture photos, N8
            Guide conversations, Soul Compass answers, Intentional Self text, daily tracking
            entries, procedure photos received from a wellness practice, and your Becoming
            information are visible only to you (and, for procedure photos, the wellness practice
            that sent them).
          </li>
          <li>
            <strong>Public only when you choose:</strong> Content you intentionally post to The
            Collective — check-ins, before/after posts, questions, replies, and reactions — is
            visible to other N8One members. Shared rituals you post as a professional are
            visible to other members.
          </li>
          <li>
            <strong>Your display name and profile picture</strong> are visible to other members
            when you post in The Collective, react, or follow someone.
          </li>
        </ul>
        <p className="mt-3">
          Private information is <em>never</em> displayed publicly unless you intentionally share
          it yourself.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">Who we share information with</h2>
        <p>
          We do not sell your personal information. We share it only with the following
          service providers, and only to the extent needed for them to help us run N8One:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-2">
          <li>
            <strong>Google (Google LLC)</strong> — for Google OAuth sign-in. Google receives what
            it needs to authenticate you.
          </li>
          <li>
            <strong>Stripe, Inc.</strong> — our payment processor for Pro subscriptions. Payment
            card details are handled by Stripe under Stripe&rsquo;s own terms and privacy notice;
            we receive only the subscription status and transaction identifiers.
          </li>
          <li>
            <strong>Anthropic, PBC</strong> — provider of the Claude model that powers N8 Guide,
            reflections, and moderation assistance. When you send a message to N8 Guide, or when
            an admin uses the moderation-assist feature on a photo you posted to The Collective,
            the relevant text and (for the moderation queue only) the two photos are sent to
            Anthropic to generate a response. Anthropic acts as our processor for this purpose.
          </li>
          <li>
            <strong>ElevenLabs, Inc.</strong> — provider of the text-to-speech voice used for
            spoken affirmations. When you play an affirmation aloud, the text is sent to
            ElevenLabs to generate audio.
          </li>
          <li>
            <strong>Emergent (Emergent Labs, Inc.)</strong> — provides the object-storage
            infrastructure where uploaded photos are kept and the routing layer to Anthropic and
            other model providers.
          </li>
          <li>
            <strong>MongoDB, Inc. (MongoDB Atlas)</strong> — hosted database where N8One data is
            stored.
          </li>
          <li>
            <strong>Wellness practice you have authorized</strong> — a wellness practice you
            explicitly consent to can send Before/After procedure photos into your Tracking tab
            and see the reactions you leave on those photos.
          </li>
          <li>
            <strong>Legal &amp; safety</strong> — we may disclose information if we&rsquo;re
            required to by law, subpoena, or valid legal request, or to protect the safety of
            our members or the public.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl">How we store and protect information</h2>
        <p>
          Your information is stored in MongoDB Atlas databases and in object storage operated by
          Emergent, both hosted on cloud infrastructure in the United States. Access to
          production systems is limited to authorized personnel. Communication between your
          browser and our servers uses HTTPS.
        </p>
        <p>
          We do not claim any particular security certification. We have not been audited under
          SOC 2, HIPAA, ISO 27001, or any similar framework. No online service can guarantee
          absolute security; we take reasonable steps to protect your information, but you use
          the service knowing this.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">How long we keep information</h2>
        <p>
          We keep account and content information for as long as your account exists. When you
          ask us to delete your account, we delete your profile, journal entries, tracking data,
          rituals, uploaded photos, and Collective posts within a reasonable period, except
          where we are required by law to retain limited records (for example, tax records
          related to subscription payments).
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">Your rights and choices</h2>
        <ul className="list-disc pl-6 mt-2 space-y-2">
          <li>
            <strong>See your data.</strong> You can view what N8One holds about you from inside
            the app (Profile, Becoming, Journal, Tracking, Rituals). To request a portable copy,
            email us.
          </li>
          <li>
            <strong>Correct your data.</strong> You can edit your profile, journal entries,
            tracking values, and rituals directly in the app.
          </li>
          <li>
            <strong>Delete individual items.</strong> Journal entries, tracking entries,
            Collective posts, uploaded photos, and rituals can be deleted from inside the app.
          </li>
          <li>
            <strong>Delete your account.</strong> Email us at{" "}
            <a href="mailto:n8onelife@gmail.com" className="underline">n8onelife@gmail.com</a>{" "}
            from the address associated with your Google sign-in and we will delete your account
            and associated content.
          </li>
          <li>
            <strong>Revoke wellness practice access.</strong> You can accept, deny, or revoke a
            wellness practice&rsquo;s access to send you Before/After photos at any time from
            your Profile.
          </li>
          <li>
            <strong>Opt out of push notifications</strong> in your browser settings or in the
            app&rsquo;s notification preferences.
          </li>
        </ul>
        <p className="mt-3">
          If you are in the European Union, United Kingdom, or a US state with a comprehensive
          privacy law (such as California, Colorado, Connecticut, Virginia, Utah, or Oregon), you
          may have additional rights — including the right to object to processing, restrict
          processing, or lodge a complaint with your local supervisory authority. Email us and
          we will honor requests to the extent required by applicable law.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">Children</h2>
        <p>
          N8One is not for anyone under 18. We do not knowingly collect information from anyone
          under 18. If you believe a child has created an account, email us and we will remove it.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">International users</h2>
        <p>
          N8One is operated from the United States, and information is stored on servers located
          in the United States. If you are accessing the app from outside the United States, you
          understand that your information will be transferred to and processed in the United
          States, which may have different data-protection laws than your country.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we make a material change we
          will update the &ldquo;Effective&rdquo; date at the top of this page and, when
          appropriate, notify you inside the app or by email. Your continued use of N8One after
          the change means you accept the updated policy.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">Contact</h2>
        <p>
          Questions or requests about this Privacy Policy:
        </p>
        <p className="mt-2">
          Essentialist Dispatch<br />
          1432 Jackson Rd<br />
          Ashland, OR 97520<br />
          <a href="mailto:n8onelife@gmail.com" className="underline">n8onelife@gmail.com</a>
        </p>
      </section>
    </LegalLayout>
  );
}
